/**
 * POSRefundFlow - Refund functionality for POS
 * Allows cashiers to look up orders and issue refunds
 */
import { useState, useEffect } from 'react';
import { 
  Search, ArrowLeft, RotateCcw, AlertTriangle, Check, 
  Printer, QrCode, X, Loader2, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { posApi } from '@/utils/apiClient';
import { FiscalReceiptDisplay, printFiscalReceipt } from '@/components/frcs/FiscalReceipt';

export default function POSRefundFlow({ onClose, cashierName = 'Admin' }) {
  const [step, setStep] = useState('search'); // search, confirm, complete
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [originalInvoice, setOriginalInvoice] = useState(null);
  const [refundInvoice, setRefundInvoice] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);

  // Load recent invoices on mount
  useEffect(() => {
    fetchRecentInvoices();
  }, []);

  const fetchRecentInvoices = async () => {
    try {
      const res = await posApi.get('/frcs-vms/fiscal-invoices?limit=10&invoice_type=NS');
      setRecentInvoices(res.data.invoices || []);
    } catch (err) {
      console.error('Failed to fetch recent invoices:', err);
    }
  };

  const searchInvoice = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter an SDC number or order number');
      return;
    }

    setSearching(true);
    try {
      // Try searching by SDC number first
      const res = await posApi.get(`/frcs-vms/fiscal-invoice/${searchQuery.trim()}`);
      const invoice = res.data;
      
      // Check if it's already a refund
      if (['NR', 'AR', 'CR', 'PR', 'TR'].includes(invoice.invoice_type)) {
        toast.error('Cannot refund a refund invoice');
        return;
      }
      
      // Check if it's a non-fiscal invoice
      if (['CS', 'CR', 'PS', 'PR', 'TS', 'TR'].includes(invoice.invoice_type)) {
        toast.error('Cannot refund non-fiscal invoices (copy/proforma/training)');
        return;
      }
      
      setOriginalInvoice(invoice);
      setStep('confirm');
    } catch (err) {
      toast.error('Invoice not found');
    } finally {
      setSearching(false);
    }
  };

  const selectInvoice = (invoice) => {
    if (['NR', 'AR'].includes(invoice.invoice_type)) {
      toast.error('Cannot refund a refund invoice');
      return;
    }
    setOriginalInvoice(invoice);
    setStep('confirm');
  };

  const processRefund = async () => {
    if (!originalInvoice) return;
    
    if (!refundReason.trim()) {
      toast.error('Please enter a reason for the refund');
      return;
    }

    setProcessing(true);
    try {
      const params = new URLSearchParams({
        cashier_name: cashierName,
        reason: refundReason.trim()
      });

      const res = await posApi.post(`/frcs-vms/refund/${originalInvoice.sdc_number}?${params.toString()}`);
      
      if (res.data.success) {
        setRefundInvoice(res.data.fiscal_invoice);
        setStep('complete');
        toast.success('Refund processed successfully');
      } else {
        throw new Error(res.data.error || 'Refund failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Refund processing failed');
    } finally {
      setProcessing(false);
    }
  };

  const resetFlow = () => {
    setStep('search');
    setSearchQuery('');
    setOriginalInvoice(null);
    setRefundInvoice(null);
    setRefundReason('');
  };

  // Search Step
  if (step === 'search') {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={onClose}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Issue Refund</h1>
                <p className="text-gray-400 text-sm">Search for the original invoice</p>
              </div>
            </div>
            <RotateCcw className="w-8 h-8 text-red-400" />
          </div>

          {/* Search Box */}
          <div className="bg-gray-800 rounded-xl p-6 mb-6">
            <label className="block text-sm text-gray-400 mb-2">
              Enter SDC Number
            </label>
            <div className="flex gap-3">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchInvoice()}
                placeholder="e.g., AX3K624F-787FED18-1"
                className="flex-1 bg-gray-700 border-gray-600 text-white text-lg font-mono"
                data-testid="refund-search-input"
              />
              <Button 
                onClick={searchInvoice}
                disabled={searching}
                className="bg-teal-600 hover:bg-teal-700 px-6"
                data-testid="refund-search-btn"
              >
                {searching ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Recent Invoices */}
          <div className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Sales</h3>
            {recentInvoices.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No recent invoices</p>
            ) : (
              <div className="space-y-2">
                {recentInvoices.map((invoice) => (
                  <button
                    key={invoice.sdc_number}
                    onClick={() => selectInvoice(invoice)}
                    className="w-full flex items-center justify-between p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
                    data-testid={`recent-invoice-${invoice.sdc_number}`}
                  >
                    <div>
                      <p className="font-mono text-sm text-teal-400">
                        {invoice.sdc_number}
                      </p>
                      <p className="text-gray-400 text-xs mt-1">
                        {invoice.sdc_time} • {invoice.cashier}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">${invoice.total?.toFixed(2)}</p>
                      <p className="text-xs text-gray-400">
                        {invoice.items?.length || 0} items
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Confirm Step
  if (step === 'confirm' && originalInvoice) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={resetFlow}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Confirm Refund</h1>
                <p className="text-gray-400 text-sm">Review and process the refund</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original Invoice */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-teal-400" />
                Original Invoice
              </h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-400">SDC Number:</span>
                  <span className="font-mono">{originalInvoice.sdc_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Date:</span>
                  <span>{originalInvoice.sdc_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Cashier:</span>
                  <span>{originalInvoice.cashier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment:</span>
                  <span>{originalInvoice.payment_method}</span>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <h4 className="text-sm text-gray-400 mb-2">Items to Refund:</h4>
                {originalInvoice.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span>{item.quantity}x {item.name}</span>
                    <span>${item.total?.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-700 mt-4 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Refund:</span>
                  <span className="text-red-400">-${originalInvoice.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400 mt-1">
                  <span>Tax Refund:</span>
                  <span>-${originalInvoice.total_tax?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Refund Details */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Refund Details
              </h3>

              <div className="mb-6">
                <label className="block text-sm text-gray-400 mb-2">
                  Reason for Refund *
                </label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="e.g., Customer changed mind, Wrong order, Quality issue..."
                  className="w-full h-32 bg-gray-700 border border-gray-600 rounded-lg p-3 text-white resize-none"
                  data-testid="refund-reason-input"
                />
              </div>

              <div className="bg-amber-900/30 border border-amber-600 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-300">Warning</p>
                    <p className="text-sm text-amber-200/80 mt-1">
                      This will create a fiscal refund invoice. This action cannot be undone.
                      The refund will reference the original invoice.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={resetFlow}
                  className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={processRefund}
                  disabled={processing || !refundReason.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="process-refund-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Process Refund
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Complete Step
  if (step === 'complete' && refundInvoice) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-900 to-red-950 flex items-center justify-center p-6">
        <div className="text-center text-white max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-500 flex items-center justify-center">
            <Check className="w-12 h-12" />
          </div>
          
          <h1 className="text-3xl font-bold mb-2">Refund Complete</h1>
          <p className="text-red-200 mb-6">
            Refund invoice has been generated
          </p>

          <div className="bg-white/10 rounded-xl p-6 mb-6 text-left">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-red-200">SDC Number:</span>
                <span className="font-mono">{refundInvoice.sdc_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-200">Reference:</span>
                <span className="font-mono text-sm">{refundInvoice.reference_number}</span>
              </div>
              <div className="flex justify-between text-xl font-bold">
                <span>Refund Total:</span>
                <span>-${refundInvoice.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              onClick={() => printFiscalReceipt(refundInvoice)}
              className="w-full bg-white/20 hover:bg-white/30 text-white"
              data-testid="print-refund-btn"
            >
              <Printer className="w-5 h-5 mr-2" />
              Print Refund Receipt
            </Button>
            <Button
              onClick={onClose}
              className="w-full bg-white text-red-900 hover:bg-white/90"
              data-testid="close-refund-btn"
            >
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
